import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { useT } from '../i18n';
import SignupPlanPicker, { SignupPlanSummary } from './SignupPlanPicker';
import SignalGlobe from './SignalGlobe';
import {
  adoptPlanFromUrl,
  readPendingPlan,
  savePendingPlan,
  signupRequestedInUrl,
} from '../lib/billingFlow';
import { fetchInstanceLicence } from '../lib/instanceLicence';
import { BACKEND_URL } from '../runtimeConfig';

// Wave 15 — env-resolved API base, mirrors App.js. We can't import from
// App.js because of the circular ``useAuth`` dep, so duplicate the trim.
const API = `${BACKEND_URL}/api`;

export default function AuthForm() {
  const { login, verify2fa, register } = useAuth();
  const { t } = useT();
  const [showPassword, setShowPassword] = useState(false);
  // A link from the website (?plan=pro&interval=annual or ?signup=1) opens sign-up.
  const [activeTab, setActiveTab] = useState(() => (signupRequestedInUrl() ? 'register' : 'login'));

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', fullName: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Registration is only possible with a plan (see SignupPlanPicker).
  const [signupPlan, setSignupPlan] = useState(() => adoptPlanFromUrl() || readPendingPlan());
  // A self-hosted install has no plans to choose: its licence covers it.
  const [selfHosted, setSelfHosted] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchInstanceLicence(API).then((d) => {
      if (alive) setSelfHosted(Boolean(d && d.mode === 'self_hosted'));
    });
    return () => {
      alive = false;
    };
  }, []);
  // Signup email verification — when register (or a login attempt on an
  // unconfirmed account) reports the address still needs confirming, we
  // swap the forms for a "check your inbox" panel with a resend button.
  const [pendingVerifyEmail, setPendingVerifyEmail] = useState(null);
  const [resendState, setResendState] = useState('idle'); // idle | busy | sent
  // Wave 12 — 2FA challenge state. When the backend says
  // ``requires_2fa=true`` we stash the challenge token here and swap
  // the form for a TOTP / backup-code prompt.
  const [twoFa, setTwoFa] = useState({ challenge: null, code: '' });
  // Wave 15 — list of SSO providers the backend has credentials for.
  // Empty list (or fetch failure) hides the SSO buttons entirely.
  const [ssoProviders, setSsoProviders] = useState([]);
  // Operator kill-switch from the backend. Defaults to ``true`` so the
  // UI keeps working against older backends that don't expose this
  // endpoint yet; we flip it off only when the server explicitly says
  // ``enabled: false``.
  const [registrationEnabled, setRegistrationEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!API.startsWith('/')) {
      axios.get(`${API}/auth/sso/providers`).then(
        (r) => {
          if (!cancelled) {
            const list = (r.data && r.data.providers) || [];
            setSsoProviders(list.map((p) => p.provider).filter(Boolean));
          }
        },
        () => {
          // Silent — older backends don't expose this endpoint.
          if (!cancelled) setSsoProviders([]);
        },
      );
      axios.get(`${API}/auth/registration-status`).then(
        (r) => {
          if (cancelled) return;
          const enabled = r.data && r.data.enabled !== false;
          setRegistrationEnabled(enabled);
          if (!enabled) setActiveTab('login');
        },
        () => {
          // Silent — older backends predate the toggle and behave as open.
        },
      );
    }
    return () => {
      cancelled = true;
    };
  }, []);

  // Wave 15 — surface upstream SSO errors that the backend forwarded
  // via fragment (``#sso_error=...&sso_error_description=...``).
  useEffect(() => {
    const hash = (typeof window !== 'undefined' && window.location.hash) || '';
    if (!hash || !hash.includes('sso_error=')) return;
    const params = new URLSearchParams(hash.replace(/^#/, ''));
    const code = params.get('sso_error');
    const desc = params.get('sso_error_description');
    if (code) {
      setError(desc ? `${t('SSO failed')}: ${desc}` : t('SSO failed'));
      // Clean the URL so a refresh doesn't re-trigger the toast.
      try {
        window.history.replaceState(null, '', window.location.pathname);
      } catch (_e) {
        /* no-op */
      }
    }
  }, []);

  const handleSsoStart = (provider) => {
    if (!provider) return;
    setError('');
    setLoading(true);
    axios.get(`${API}/auth/sso/${provider}/login`).then(
      (r) => {
        const url = r.data && r.data.authorization_url;
        if (url) {
          window.location.assign(url);
        } else {
          setError(t('SSO is not configured'));
          setLoading(false);
        }
      },
      (err) => {
        setError(err.response?.data?.detail || t('SSO is not configured'));
        setLoading(false);
      },
    );
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(loginData.email, loginData.password);
    if (result?.requires_2fa) {
      setTwoFa({ challenge: result.challenge_token, code: '' });
    } else if (result?.email_not_verified) {
      setResendState('idle');
      setPendingVerifyEmail(result.email || loginData.email);
    } else if (!result?.ok) {
      setError(result?.detail || t('Invalid email or password'));
    }
    setLoading(false);
  };

  const handleTwoFa = async (e) => {
    e.preventDefault();
    if (!twoFa.challenge || !twoFa.code) return;
    setError('');
    setLoading(true);
    const ok = await verify2fa(twoFa.challenge, twoFa.code.trim());
    if (!ok) {
      setError(t('Invalid 2FA code'));
    } else {
      setTwoFa({ challenge: null, code: '' });
    }
    setLoading(false);
  };

  const choosePlan = (choice) => {
    savePendingPlan(choice.plan, choice.interval);
    setSignupPlan(choice);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    // After signup BillingGate sends the account straight to Stripe Checkout.
    if (signupPlan && !selfHosted) savePendingPlan(signupPlan.plan, signupPlan.interval);
    const success = await register(registerData.email, registerData.password, registerData.fullName);
    if (success?.verification_required) {
      setResendState('idle');
      setPendingVerifyEmail(success.email || registerData.email);
    } else if (!success) {
      setError(t('Registration failed. Please try again.'));
    }
    setLoading(false);
  };

  const handleResendVerification = async () => {
    if (!pendingVerifyEmail || resendState === 'busy') return;
    setResendState('busy');
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: pendingVerifyEmail });
      setResendState('sent');
    } catch (_e) {
      // Anti-enumeration endpoint practically never errors; treat a
      // network hiccup as retryable.
      setResendState('idle');
      setError(t('Could not resend the email — try again.'));
    }
  };

  const isLoginInvalid = !loginData.email.trim() || !loginData.password;
  const isRegisterInvalid =
    !registerData.email.trim() ||
    registerData.password.length < 8 ||
    registerData.password.length > 128 ||
    !registerData.fullName.trim();

  return (
    <div className="xp-app xp-app--auth">
      <main className="xp-main">
        {/* Top bar: just the logo, no workspace sidebar before sign-in */}
        <div className="xp-topbar">
          <a href="https://xpnsr.tech" className="xp-auth-logo" aria-label="xpnsr.tech">
            <span className="xp-brand-logo">xpnsr.</span>
          </a>
          <a href="https://xpnsr.tech" className="xp-auth-site">xpnsr.tech ↗</a>
        </div>

        <section className="xp-auth-main">
          <SignalGlobe className="xp-auth-globe" />
          <div className="xp-auth-wrap">
            <div className="xp-card">
              {/* Terminal chrome */}
              <div className="xp-chrome">
                <div className="xp-chrome-dots">
                  <i /><i /><i />
                </div>
                <div className="xp-chrome-path">auth-tracker.xpnsr.tech</div>
                <div className="xp-chrome-tls">TLS 1.3</div>
              </div>

              <div className="xp-card-body">
                {/* Tabs */}
                <div className="xp-tabs">
                  <button
                    type="button"
                    className={activeTab === 'login' ? 'is-on' : ''}
                    onClick={() => setActiveTab('login')}
                    data-testid="tab-login"
                  >
                    {t('Sign in')}
                  </button>
                  {registrationEnabled && (
                    <button
                      type="button"
                      className={activeTab === 'register' ? 'is-on' : ''}
                      onClick={() => setActiveTab('register')}
                      data-testid="tab-register"
                    >
                      {t('Create account')}
                    </button>
                  )}
                </div>

                {!registrationEnabled && (
                  <p
                    className="xp-help"
                    data-testid="registration-disabled-notice"
                    style={{ marginTop: 8 }}
                  >
                    {t('New registrations are disabled. Contact your workspace admin for an invitation.')}
                  </p>
                )}

                {activeTab === 'login' ? (
                  <h1 className="xp-h1">
                    {t('Attribution,')} <em>{t('honest')}</em>.
                  </h1>
                ) : (
                  <h1 className="xp-h1">
                    {t('Create')} <em>{t('account')}</em>.
                  </h1>
                )}
                <p className="xp-card-sub">
                  Tracker · <b>{t('S2S postback · bot shield')}</b>
                </p>

                {error && (
                  <div className="xp-alert" role="alert">
                    <span>⚠</span> {error}
                  </div>
                )}

                {pendingVerifyEmail ? (
                  <div data-testid="verify-email-pending">
                    <p className="xp-help" style={{ marginTop: 12 }}>
                      {t('We sent a confirmation link to {email}. Open it to activate your account — the link is valid for 24 hours.', { email: pendingVerifyEmail })}
                    </p>
                    <p className="xp-help" style={{ marginTop: 8 }}>
                      {t("Check your spam folder if it doesn't arrive within a minute.")}
                    </p>
                    <button
                      type="button"
                      className="xp-btn-main"
                      style={{ marginTop: 16 }}
                      disabled={resendState === 'busy'}
                      onClick={handleResendVerification}
                      data-testid="resend-verification"
                    >
                      {resendState === 'busy'
                        ? t('Please wait…')
                        : resendState === 'sent'
                          ? t('Sent — check your inbox')
                          : t('Resend the email')}
                    </button>
                    <button
                      type="button"
                      className="xp-btn-ghost"
                      style={{ marginTop: 8 }}
                      onClick={() => {
                        setPendingVerifyEmail(null);
                        setResendState('idle');
                        setActiveTab('login');
                      }}
                      data-testid="verify-back-to-login"
                    >
                      {t('Back to sign in')}
                    </button>
                  </div>
                ) : activeTab === 'login' && twoFa.challenge ? (
                  <form onSubmit={handleTwoFa} noValidate>
                    <div className="xp-f">
                      <label>{t('Two-factor code')}</label>
                      <input
                        type="text"
                        value={twoFa.code}
                        onChange={(e) =>
                          setTwoFa((prev) => ({ ...prev, code: e.target.value }))
                        }
                        placeholder="123456 or BACKUP-CODE9"
                        inputMode="text"
                        autoComplete="one-time-code"
                        autoFocus
                        required
                        data-testid="login-2fa-code"
                      />
                      <div className="xp-help">
                        {t(
                          'Enter the 6-digit code from your authenticator app, or one of your backup codes.',
                        )}
                      </div>
                    </div>
                    <div className="xp-row">
                      <button
                        type="button"
                        className="xp-btn ghost"
                        onClick={() => setTwoFa({ challenge: null, code: '' })}
                      >
                        {t('Back')}
                      </button>
                      <button
                        type="submit"
                        className="xp-btn primary"
                        disabled={loading || !twoFa.code.trim()}
                        data-testid="login-2fa-submit"
                      >
                        {loading ? t('Verifying…') : t('Verify')}
                      </button>
                    </div>
                  </form>
                ) : activeTab === 'login' ? (
                  <form onSubmit={handleLogin} noValidate>
                    <div className="xp-f">
                      <label>{t('Work email')}</label>
                      <input
                        type="email"
                        value={loginData.email}
                        onChange={(e) =>
                          setLoginData((prev) => ({ ...prev, email: e.target.value }))
                        }
                        placeholder="you@company.com"
                        autoComplete="email"
                        spellCheck={false}
                        required
                        data-testid="login-email"
                      />
                    </div>

                    <div className="xp-f">
                      <label>{t('Password')}</label>
                      <div className="xp-pw">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={loginData.password}
                          onChange={(e) =>
                            setLoginData((prev) => ({ ...prev, password: e.target.value }))
                          }
                          placeholder="••••••••••"
                          autoComplete="current-password"
                          required
                          data-testid="login-password"
                        />
                        <button
                          type="button"
                          className="xp-eye"
                          onClick={() => setShowPassword((s) => !s)}
                          aria-label={showPassword ? t('Hide password') : t('Show password')}
                        >
                          {showPassword ? 'HIDE' : 'SHOW'}
                        </button>
                      </div>
                    </div>

                    <div className="xp-f-row">
                      <label>
                        <input type="checkbox" defaultChecked />
                        {t('Remember this device')}
                      </label>
                      <a href="#reset" onClick={(e) => e.preventDefault()}>
                        {t('Reset')}
                      </a>
                    </div>

                    <button
                      type="submit"
                      className="xp-btn-main"
                      disabled={loading || isLoginInvalid}
                      data-testid="login-submit"
                    >
                      {loading ? t('Signing in…') : `${t('Access tracker')} →`}
                    </button>

                    {registrationEnabled && (
                      <button
                        type="button"
                        className="xp-btn-ghost"
                        onClick={() => setActiveTab('register')}
                        data-testid="goto-register"
                      >
                        {t('Create new account')}
                      </button>
                    )}

                    {ssoProviders.length > 0 && (
                      <div className="xp-sso-row" data-testid="sso-row">
                        <div className="xp-sso-divider">
                          <span>{t('or continue with')}</span>
                        </div>
                        {ssoProviders.includes('google') && (
                          <button
                            type="button"
                            className="xp-btn-ghost"
                            onClick={() => handleSsoStart('google')}
                            disabled={loading}
                            data-testid="sso-google"
                          >
                            {t('Sign in with Google')}
                          </button>
                        )}
                        {ssoProviders.includes('microsoft') && (
                          <button
                            type="button"
                            className="xp-btn-ghost"
                            onClick={() => handleSsoStart('microsoft')}
                            disabled={loading}
                            data-testid="sso-microsoft"
                          >
                            {t('Sign in with Microsoft')}
                          </button>
                        )}
                      </div>
                    )}
                  </form>
                ) : !signupPlan && !selfHosted ? (
                  <SignupPlanPicker onContinue={choosePlan} />
                ) : (
                  <form onSubmit={handleRegister} noValidate>
                    {!selfHosted && (
                      <SignupPlanSummary plan={signupPlan} onChange={() => setSignupPlan(null)} />
                    )}
                    <div className="xp-f">
                      <label>{t('Full name')}</label>
                      <input
                        type="text"
                        value={registerData.fullName}
                        onChange={(e) =>
                          setRegisterData((prev) => ({ ...prev, fullName: e.target.value }))
                        }
                        placeholder={t('Jane Doe')}
                        required
                        data-testid="register-fullname"
                      />
                    </div>

                    <div className="xp-f">
                      <label>{t('Work email')}</label>
                      <input
                        type="email"
                        value={registerData.email}
                        onChange={(e) =>
                          setRegisterData((prev) => ({ ...prev, email: e.target.value }))
                        }
                        placeholder="you@company.com"
                        autoComplete="email"
                        spellCheck={false}
                        required
                        data-testid="register-email"
                      />
                    </div>

                    <div className="xp-f">
                      <label>{t('Password')}</label>
                      <div className="xp-pw">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={registerData.password}
                          onChange={(e) =>
                            setRegisterData((prev) => ({ ...prev, password: e.target.value }))
                          }
                          placeholder="••••••••••"
                          required
                          minLength={8}
                          maxLength={128}
                          data-testid="register-password"
                        />
                        <button
                          type="button"
                          className="xp-eye"
                          onClick={() => setShowPassword((s) => !s)}
                          aria-label={showPassword ? t('Hide password') : t('Show password')}
                        >
                          {showPassword ? 'HIDE' : 'SHOW'}
                        </button>
                      </div>
                      <p className="xp-help">{t('Use 8–128 characters; long passphrases are recommended')}</p>
                    </div>

                    <button
                      type="submit"
                      className="xp-btn-main"
                      disabled={loading || isRegisterInvalid}
                      data-testid="register-submit"
                    >
                      {loading ? t('Creating account…') : `${t('Create account')} →`}
                    </button>

                    <button
                      type="button"
                      className="xp-btn-ghost"
                      onClick={() => setActiveTab('login')}
                      data-testid="goto-login"
                    >
                      {t('Already have an account? Sign in')}
                    </button>
                  </form>
                )}

                <div className="xp-status">
                  <div className="xp-sr"><b>session</b>&nbsp;encrypted · TLS 1.3</div>
                  <div className="xp-sr"><b>postback</b>&nbsp;endpoints · ready</div>
                  <div className="xp-sr"><b>bot shield</b>&nbsp;live · 99.7% filtered</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
